import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError, useAuth } from "@/lib/shared";

export const LoginPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate(from, { replace: true }); }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient gold glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-yellow-500/10 via-amber-600/5 to-transparent blur-3xl gold-shimmer pointer-events-none" />
      <Card className="w-full max-w-md bg-card/80 backdrop-blur-xl gold-border gold-glow relative z-10">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-32 h-32 mb-2 flex items-center justify-center">
            <img src="/eliteiv-logo.png" alt="EliteIV Lounge" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" />
          </div>
          <CardTitle className="text-3xl font-bold brand-heading tracking-wide">
            <span className="gold-gradient-text">Elite</span><span className="silver-gradient-text">IV</span>
            <span className="block text-sm font-normal text-muted-foreground tracking-[0.3em] uppercase mt-1">EMR System</span>
          </CardTitle>
          <CardDescription className="text-muted-foreground/80">Staff Login — Secure Access</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="bg-destructive/15 border border-destructive/30 text-destructive text-sm p-3 rounded-lg" data-testid="login-error">{error}</div>}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-silver">Email</Label>
              <Input id="email" type="email" placeholder="you@eliteiv.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="bg-muted/40 border-border/60 focus:border-primary focus:ring-primary/30" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-silver">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="bg-muted/40 border-border/60 focus:border-primary focus:ring-primary/30 pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-400 hover:via-yellow-400 hover:to-amber-500 text-black font-semibold tracking-wide shadow-lg shadow-amber-500/20 transition-all" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in..." : "Login to EliteIV EMR"}
            </Button>
          </form>
          <p className="text-center text-xs text-muted-foreground/60 mt-6 tracking-wider">Secure access for EliteIV staff only</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== HELPERS ====================
